import { useLocation, useNavigate, useParams } from "react-router";

import type { ComponentType } from "react";
import type { Location, NavigateFunction, Params as RouterParams, To } from "react-router";

/** Minimal history-shaped API for class components migrated from RR v5. */
export interface RouterHistory {
  push: (to: To, state?: unknown) => void;
  replace: (to: To, state?: unknown) => void;
  go: (n: number) => void;
  goBack: () => void;
  goForward: () => void;
  location: Location;
}

export interface RouterMatch<
  TParams extends { [K in keyof TParams]?: string } = Record<string, string | undefined>,
> {
  params: TParams;
  path: string;
  url: string;
  isExact: boolean;
}

/** Drop-in replacement for RR v5 `RouteComponentProps`. */
export interface RouteComponentProps<
  TParams extends { [K in keyof TParams]?: string } = Record<string, string | undefined>,
> {
  history: RouterHistory;
  location: Location;
  match: RouterMatch<TParams>;
  navigate: NavigateFunction;
}

function createHistory(navigate: NavigateFunction, location: Location): RouterHistory {
  return {
    push: (to, state) => {
      navigate(to, { state: state as never });
    },
    replace: (to, state) => {
      navigate(to, { replace: true, state: state as never });
    },
    go: (n) => {
      navigate(n);
    },
    goBack: () => {
      navigate(-1);
    },
    goForward: () => {
      navigate(1);
    },
    location,
  };
}

function createMatch<TParams extends Record<string, string | undefined>>(
  params: Readonly<RouterParams>,
  pathname: string,
): RouterMatch<TParams> {
  return {
    params: params as TParams,
    path: pathname,
    url: pathname,
    isExact: true,
  };
}

/**
 * Compatibility HOC for class / connect() wrappers that still expect
 * `history`, `location`, and `match` from React Router v5.
 */
export function withRouter<P extends RouteComponentProps>(
  Component: ComponentType<P>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): ComponentType<any> {
  const WithRouter = (props: Omit<P, keyof RouteComponentProps>) => {
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();
    const history = createHistory(navigate, location);
    const match = createMatch(params, location.pathname);

    return (
      <Component
        {...(props as P)}
        history={history}
        location={location}
        match={match}
        navigate={navigate}
      />
    );
  };

  WithRouter.displayName = `withRouter(${Component.displayName ?? Component.name ?? "Component"})`;
  return WithRouter;
}
